import React, { useState } from 'react';
import { Lock, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/ui/button';

/* Faithful visual re-creation of the ACQ merchant-portal sign-in using the new
   brand logo and the blue diamond hero background. No auth wired. */

const HERO_BG = 'https://customer-assets.emergentagent.com/job_3f9e40e3-cca9-4507-83e7-20ced098354a/artifacts/poee8d1c_B.png';
const ECOMM_LOGO = 'https://customer-assets.emergentagent.com/job_dc63fc88-a4c1-4ec0-a429-62db7388c03c/artifacts/qlcn8omg_ECOMM%20LOGO.PNG';

const AcqPortalLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = (e) => {
    e.preventDefault();
    setError('Portal connection not enabled in this preview.');
  };

  return (
    <div
      className="min-h-screen bg-white bg-cover bg-right-top bg-no-repeat"
      style={{ backgroundImage: `url(${HERO_BG})` }}
      data-testid="acq-portal-login-page"
    >
      {/* Top strip — logo right, clickable → home */}
      <div className="bg-[#1F4E79] h-20 flex items-center">
        <div className="max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 flex items-center justify-end">
          <a
            href="/"
            aria-label="Back to eCOMM home"
            className="transition-opacity duration-200 hover:opacity-80"
            data-testid="acq-portal-logo-home"
          >
            <img
              src={ECOMM_LOGO}
              alt="eCOMM Merchant Solutions"
              className="h-10 w-auto brightness-0 invert"
            />
          </a>
        </div>
      </div>

      {/* Sign-in card — left-aligned so the diamond artwork shows on the right */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 min-h-[calc(100vh-5rem)] flex items-start lg:items-center">
        <div className="w-full max-w-md">
          <div className="bg-white rounded-lg shadow-2xl border border-gray-200 overflow-hidden" data-testid="acq-portal-card">
            <div className="bg-[#1F4E79] px-8 py-5 flex items-center justify-center">
              <img
                src={ECOMM_LOGO}
                alt="eCOMM"
                className="h-8 w-auto brightness-0 invert"
              />
            </div>

            <div className="px-8 py-8">
              <h1 className="text-2xl font-bold text-gray-900 mb-1" data-testid="acq-portal-title">
                Acquiring Platform
              </h1>
              <p className="text-sm text-gray-500 mb-6">Please sign in to continue</p>

              <form onSubmit={onSubmit} className="space-y-4" data-testid="acq-portal-form">
                <div>
                  <label htmlFor="acq-username" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Username / Email</label>
                  <div className="relative">
                    <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      id="acq-username"
                      type="text"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoFocus
                      className="w-full pl-9 pr-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4E79]/30 focus:border-[#1F4E79]"
                      data-testid="acq-portal-username"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="acq-password" className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-1.5">Password</label>
                  <div className="relative">
                    <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      id="acq-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      className="w-full pl-9 pr-10 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#1F4E79]/30 focus:border-[#1F4E79]"
                      data-testid="acq-portal-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Toggle password visibility"
                      data-testid="acq-portal-toggle-password"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between text-sm">
                  <label className="flex items-center gap-2 text-gray-700 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={remember}
                      onChange={(e) => setRemember(e.target.checked)}
                      className="rounded border-gray-300 text-[#1F4E79] focus:ring-[#1F4E79]/30"
                      data-testid="acq-portal-remember"
                    />
                    Remember me
                  </label>
                  <a
                    href="#"
                    onClick={(e) => e.preventDefault()}
                    className="text-[#1F4E79] hover:underline font-semibold"
                    data-testid="acq-portal-forgot"
                  >
                    Forgot password?
                  </a>
                </div>

                {error && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900" data-testid="acq-portal-error">
                    {error}
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full bg-[#1F4E79] hover:bg-[#163a5f] text-white py-2.5 text-sm font-semibold uppercase tracking-wider"
                  data-testid="acq-portal-submit"
                >
                  Sign In
                </Button>
              </form>

              <div className="mt-6 pt-5 border-t border-gray-200 text-center text-xs text-gray-500">
                Need access? Contact{' '}
                <a href="mailto:csr@ecomm365.com" className="text-[#1F4E79] font-semibold hover:underline">csr@ecomm365.com</a>
              </div>
            </div>
          </div>

          <p className="text-center text-xs text-gray-500 mt-6">
            &copy; {new Date().getFullYear()} eCOMM Merchant Solutions. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AcqPortalLogin;
